import { createSelector } from 'reselect';

// Types
import { RootState } from 'types/RootState.types';

// Config
import { PAYMENT_BLOCKERS } from '@components/payment-option-unavailable/PaymentOptionUnavailable.config';
import featureConstants, { DISABLED_FEATURE } from '@constants/featureConstants';
import { KLARNA_MAX_SPEND } from '@constants/paymentConstants';
import { isFeatureActive, isFeatureDisabled } from '@redux/reducers/config/configReducer';
import {
  isStripeCheckoutEnabledFeatureActive,
  isStripeKlarnaCheckoutEnabledFeatureActive,
} from '@redux/reducers/config/configSelector';
import { getDeliveriesWithClearabee } from '@redux/reducers/delivery/deliverySelector';
import { getOutstandingBalanceRawSelector } from '@redux/reducers/order-form/orderFormSelector';
import { getKlarnaPayCompatibilityChecksComplete } from '@redux/reducers/payment/paymentSelector';

export const shouldShowKlarnaFeature = (state: RootState): boolean => {
  const isDisabled =
    isFeatureDisabled(state, DISABLED_FEATURE.KLARNA) || isFeatureActive(state, featureConstants.DISABLE_KLARNA);

  return !isDisabled;
};

export type KlarnaEligibilityType = {
  isEligible: boolean;
  isEnabled: boolean;
  reasons: (typeof PAYMENT_BLOCKERS)[keyof typeof PAYMENT_BLOCKERS][];
};

export const selectKlarnaIneligibilityReasons = createSelector(
  [getDeliveriesWithClearabee, getOutstandingBalanceRawSelector],
  (deliveriesWithClearabee = [], outstandingBalanceRaw) => {
    const reasons: (typeof PAYMENT_BLOCKERS)[keyof typeof PAYMENT_BLOCKERS][] = [];

    if (deliveriesWithClearabee.length > 0) {
      reasons.push(PAYMENT_BLOCKERS.CLEARABEE_DELIVERIES);
    }

    if (Number(outstandingBalanceRaw) > KLARNA_MAX_SPEND) {
      reasons.push(PAYMENT_BLOCKERS.BALANCE_ABOVE_MAX);
    }

    return reasons;
  },
);

export const isBasketKlarnaEligible = createSelector(
  [
    shouldShowKlarnaFeature,
    selectKlarnaIneligibilityReasons,
    getKlarnaPayCompatibilityChecksComplete,
    isStripeCheckoutEnabledFeatureActive,
    isStripeKlarnaCheckoutEnabledFeatureActive,
  ],
  (
    shouldShowKlarnaFeature,
    reasons,
    isKlarnaPayAvailable,
    stripeCheckoutFlowEnabled,
    stripeKlarnaCheckoutFlowEnabled,
  ) => {
    const isUsingStripeFlow = stripeCheckoutFlowEnabled && stripeKlarnaCheckoutFlowEnabled;
    const isEligible = reasons.length === 0;
    const isEnabled = shouldShowKlarnaFeature && (!isUsingStripeFlow || isKlarnaPayAvailable);

    return {
      isEligible,
      isEnabled,
      reasons,
    };
  },
);
